/**
 * platform-release-notes-page.tsx — Platform → Thông báo cập nhật (super-admin). Route `/platform/release-notes`.
 *
 * Soạn bản "eCDP có gì mới" (tiêu đề, các dòng tính năng, video animation HTML tự chứa) và bật / tắt cho người dùng.
 * Người dùng thấy bản ĐANG BẬT mới nhất ở modal mỗi lần mở trang (tối đa N lần; "Không hiển thị lại" tắt hẳn).
 * Backend: `platform/release-notes-routes.ts`. Mẫu animation: `public/release-notes/2026-10-10.html`.
 */
import { useRef, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { Eye, FileUp, Loader2, Pencil, Plus, Trash2, Wand2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { ErrorState } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch, Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { platformApiError } from '@/lib/platform-client'
import {
  useDeleteReleaseNote, usePlatformReleaseNotes, useSaveReleaseNote, useToggleReleaseNote, type PlatformReleaseNote,
} from '@/hooks/use-platform-release-notes'
import sampleHtml from '../../../public/release-notes/2026-10-10.html?raw'
import { emptyReleaseForm, formToInput, noteToForm, validateReleaseForm, type ReleaseNoteForm } from './release-note-editor'

export function PlatformReleaseNotesPage() {
  const { data, isLoading, isError } = usePlatformReleaseNotes()
  const toggle = useToggleReleaseNote()
  const del = useDeleteReleaseNote()
  const [editing, setEditing] = useState<PlatformReleaseNote | 'new' | null>(null)
  const [preview, setPreview] = useState<PlatformReleaseNote | null>(null)

  const remove = (n: PlatformReleaseNote) => {
    if (!window.confirm(`Xoá bản "${n.title}"? Không hoàn tác được.`)) return
    del.mutate(n.id, { onSuccess: () => toast.success('Đã xoá'), onError: (e) => toast.error(platformApiError(e)) })
  }
  // Người dùng chỉ thấy MỘT bản: bản đang bật có ngày phát hành mới nhất.
  const live = (data ?? []).filter((n) => n.isActive && n.html).sort((a, b) => b.releasedAt.localeCompare(a.releasedAt))[0]

  const columns: Column<PlatformReleaseNote>[] = [
    {
      key: 'title', header: 'Bản cập nhật',
      cell: (n) => (
        <div className="min-w-0 max-w-[360px]">
          <p className="truncate font-medium">{n.title}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{n.slug} · {n.highlights.length} dòng tính năng</p>
        </div>
      ),
    },
    { key: 'date', header: 'Ngày phát hành', cell: (n) => <span className="whitespace-nowrap text-sm">{dayjs(n.releasedAt).format('DD/MM/YYYY')}</span> },
    { key: 'max', header: 'Hiện tối đa', align: 'right', cell: (n) => <span className="text-sm tabular-nums">{n.maxShows} lần</span> },
    {
      key: 'status', header: 'Trạng thái',
      cell: (n) => (
        <div className="flex items-center gap-2">
          <Switch
            checked={n.isActive}
            disabled={toggle.isPending && toggle.variables?.id === n.id}
            onCheckedChange={(v) => toggle.mutate({ id: n.id, isActive: v }, { onError: (e) => toast.error(platformApiError(e)) })}
            aria-label={n.isActive ? 'Tắt bản' : 'Bật bản'}
          />
          {live?.id === n.id ? <Badge variant="success">Đang hiển thị</Badge> : <Badge variant="default">{n.isActive ? 'Đã bật' : 'Tắt'}</Badge>}
        </div>
      ),
    },
    {
      key: 'actions', header: '', align: 'right',
      cell: (n) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setPreview(n)} title="Xem trước"><Eye className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(n)} title="Sửa"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="ghost" onClick={() => remove(n)} title="Xoá" disabled={del.isPending}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Thông báo cập nhật"
        description="Bản “eCDP có gì mới” hiện cho người dùng sau mỗi lần cập nhật lớn. Chỉ bản đang bật có ngày phát hành mới nhất được hiển thị."
        actions={<Button onClick={() => setEditing('new')}><Plus /> Tạo bản mới</Button>}
      />
      {isError ? <ErrorState /> : (
        <DataTable columns={columns} rows={data ?? []} rowKey={(n) => n.id} loading={isLoading} emptyTitle="Chưa có bản nào — người dùng đang thấy bản dự phòng trong mã nguồn" />
      )}
      {editing && <NoteDialog key={editing === 'new' ? 'new' : editing.id} note={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {preview && <PreviewDialog html={preview.html} title={preview.title} onClose={() => setPreview(null)} />}
    </div>
  )
}

function PreviewFrame({ html }: { html: string }) {
  return <iframe title="Xem trước" srcDoc={html} sandbox="allow-scripts" className="aspect-video w-full rounded-lg border bg-slate-900" />
}

function PreviewDialog({ html, title, onClose }: { html: string; title: string; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Xem trước: {title}</DialogTitle></DialogHeader>
        <PreviewFrame html={html} />
      </DialogContent>
    </Dialog>
  )
}

function NoteDialog({ note, onClose }: { note: PlatformReleaseNote | null; onClose: () => void }) {
  const [f, setF] = useState<ReleaseNoteForm>(() => (note ? noteToForm(note) : emptyReleaseForm()))
  const save = useSaveReleaseNote()
  const fileRef = useRef<HTMLInputElement>(null)
  const set = <K extends keyof ReleaseNoteForm>(k: K, v: ReleaseNoteForm[K]) => setF((o) => ({ ...o, [k]: v }))

  const onFile = async (file: File | undefined) => {
    if (!file) return
    set('html', await file.text())
    if (fileRef.current) fileRef.current.value = ''
  }
  const submit = () => {
    const err = validateReleaseForm(f)
    if (err) return toast.error(err)
    save.mutate({ id: note?.id, input: formToInput(f) }, {
      onSuccess: () => { toast.success(note ? 'Đã lưu' : 'Đã tạo bản mới'); onClose() },
      onError: (e) => toast.error(platformApiError(e)),
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{note ? 'Sửa bản cập nhật' : 'Tạo bản cập nhật'}</DialogTitle>
          <DialogDescription>HTML animation phải tự chứa (CSS + JS trong cùng file, không tải gì từ mạng ngoài). Có thể chèn mẫu rồi sửa kịch bản.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Tiêu đề</Label>
            <Input value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="eCDP có gì mới — khách hàng, pipeline, phân khúc AI" />
          </div>
          <div className="space-y-1.5">
            <Label>Mã bản</Label>
            <Input value={f.slug} onChange={(e) => set('slug', e.target.value)} disabled={!!note} className="font-mono" />
            <p className="text-xs text-muted-foreground">Dùng đếm “đã hiện mấy lần” ở trình duyệt người dùng — bản mới phải có mã mới.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Ngày phát hành</Label>
            <Input type="date" value={f.date} onChange={(e) => set('date', e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Các nhóm tính năng (mỗi dòng một nhóm, tối đa 12)</Label>
            <Textarea rows={5} value={f.highlightsText} onChange={(e) => set('highlightsText', e.target.value)} placeholder={'Khách hàng: gộp tab …\nPipeline: tối đa 12 bước …'} />
          </div>
          <div className="space-y-1.5">
            <Label>Hiện tối đa (lần mở trang)</Label>
            <Input type="number" min={1} max={20} value={f.maxShows} onChange={(e) => set('maxShows', e.target.value)} />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <Switch checked={f.isActive} onCheckedChange={(v) => set('isActive', v)} id="rn-active" />
            <Label htmlFor="rn-active">Bật hiển thị cho người dùng</Label>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>HTML animation</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => set('html', sampleHtml)}><Wand2 className="!size-3.5" /> Chèn mẫu</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}><FileUp className="!size-3.5" /> Tải file .html</Button>
                <input ref={fileRef} type="file" accept=".html,text/html" className="hidden" onChange={(e) => void onFile(e.target.files?.[0])} />
              </div>
            </div>
            <Textarea rows={8} value={f.html} onChange={(e) => set('html', e.target.value)} className="font-mono text-xs" placeholder="<!doctype html> …" />
          </div>
          {f.html.trim() && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Xem trước</Label>
              <PreviewFrame html={f.html} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          <Button onClick={submit} disabled={save.isPending}>{save.isPending && <Loader2 className="animate-spin" />} Lưu</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
